import React, { useCallback, useMemo } from "react"
import {
  Box,
  Button,
  useTheme,
  Avatar,
  Typography,
  CardContent,
  CardMedia,
  ListItemIcon,
} from "@mui/material"
import clsx from "clsx"

import { deepmerge } from "@mui/utils"
import { useNavigate } from "react-router-dom"
import _ from "lodash"

import { getIcon } from "utils/icon"
import { isValidURL } from "utils/url"
import { sanitizePublicPath } from "providers/venue/utils/fetchUtils"

import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { useStepByStep } from "hooks/direction/useStepByStep"
import { useStepByStepMap } from "hooks/direction/useStepByStepMap"
import { useStepTracking } from "hooks/analytics"

import {
  CloseIcon,
  ShopIcon,
  OriginMarkerIcon,
  DestinationMarkerIcon,
  PauseIcon,
  PlayArrowIcon,
  RefreshArrowIcon,
} from "components/icons"
import { PageCard } from "components/card"
import { MotionDiv } from "components/motions"
import { OrdinalSelector } from "components/OrdinalSelector"
import { CloseIconButton } from "components/Button"
import { SkeletonStepByStepSidebar } from "extensions/iconsiam/components/domains/direction/kiosk-h"
import { VerticalStepProgressBar } from "components/Progress/VerticalStepProgressBar"
import { VerticalDirectionImageDialog } from "components/Dialog/VerticalDirectionImageDialog"
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded"
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded"
import { CoolDownProgress } from "extensions/iconsiam/components/CoolDownProgress"

export const StepByStepKioskHPage = ({ Layout = Box }) => {
  const navigate = useNavigate()
  const { isMultiOrdinalVenue } = useVenue()
  const { t } = useTranslation()
  const theme = useTheme()

  const {
    isReady,
    currentStepNumber,
    currentStep,
    currentOriginStepLevelName,
    currentDestStepLevelName,
    destinationName,
    steps,
    route,
    destinationLogo,
    destinationCategoryIconPath,
    originVenueLogo,
    destinationVenueLogo,
    directionOverviewPath,
    currentVerticalStepMethod,
    // Auto play
    hasNextStep,
    hasPrevStep,
    stepAutoplayConfig,
    isPlaying,
    goToStepByIndex,
    goToNextStep,
    goToPrevStep,
    currentLevel,
    currentOrdinal,
    restartAutoPlay,
    resumeAutoPlay,
    pauseAutoPlay,
  } = useStepByStep()

  const stepsAvailable = steps && steps.length > 0

  useStepByStepMap(route, currentStepNumber, {
    offset: {
      bottom: 0,
    },
    enabled: stepsAvailable,
  })

  useStepTracking({
    currentStep: currentStepNumber,
    steps,
    enabled: isReady,
  })

  usePageSideBarLayout({
    sidebar: {
      open: true,
    },
  })

  const {
    root,
    headerContainer,
    contentContainer,
    logoContainer,
    titleText,
    venueLogo,
    stepContainer,
    logoImage,
    listItemIcon,
    actionContainer,
    pageCardSideBar,
    sidebarContent,
    ordinalSelectMenu,
    playerButton,
    stepProgressBarContainer,
    stepProgressBar,
    closeButton,
  } = theme.components[`AppStepByStep`]?.styleOverrides || {}

  const { destinationIcon, locationAvatar, originIcon, cooldownProgress } =
    theme.components[`AppNavigationStep`]?.styleOverrides || {}

  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}
  const { wrapper, body } = sideBarStyleOverrides

  const closeIcon = useMemo(
    () => getIcon(theme?.assets?.closeIcon) || <CloseIcon />,
    [theme]
  )

  const destinationIconComp = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.destination, destinationIcon) || (
        <ShopIcon sx={destinationIcon} />
      ),
    [theme, destinationIcon]
  )

  const originMarkerIcon = useMemo(
    () =>
      (getIcon(theme?.assets?.navigation?.origin, originIcon) || (
        <OriginMarkerIcon sx={originIcon} />
      )) as JSX.Element,
    [theme, originIcon]
  )

  const destinationStepIcon = useMemo(
    () =>
      (getIcon(theme?.assets?.directionStep?.destination, destinationIcon) || (
        <DestinationMarkerIcon
          color="error"
          fontSize="small"
          sx={destinationIcon}
        />
      )) as JSX.Element,
    [theme, destinationIcon]
  )

  const prevIcon = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.prevIcon) || (
        <KeyboardArrowLeftRoundedIcon />
      ),
    [theme]
  )
  const nextIcon = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.nextIcon) || (
        <KeyboardArrowRightRoundedIcon />
      ),
    [theme]
  )

  const verticalDirectionImageUrl = useMemo(() => {
    const src = _.get(
      theme?.assets?.verticalStep?.[currentVerticalStepMethod],
      "url",
      ""
    )
    return isValidURL(src) ? src : sanitizePublicPath(src)
  }, [currentVerticalStepMethod, theme?.assets?.verticalStep])

  const showVerticalStepDialog = !!verticalDirectionImageUrl

  const handleClose = useCallback(() => {
    navigate(directionOverviewPath, {
      replace: true,
    })
  }, [navigate, directionOverviewPath])

  const isRtl = theme.isRtl

  return (
    //@ts-ignore
    <Layout
      aside={
        <PageCard sx={pageCardSideBar}>
          <MotionDiv
            sx={deepmerge(wrapper, sidebarContent)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
          >
            <Box sx={body}>
              {!stepsAvailable ? (
                <SkeletonStepByStepSidebar />
              ) : (
                <Box sx={root}>
                  <CardContent sx={contentContainer}>
                    <CloseIconButton
                      sx={closeButton}
                      icon={closeIcon}
                      onClick={handleClose}
                      size="large"
                    />
                    <Box sx={headerContainer}>
                      <Box sx={logoContainer}>
                        <Avatar
                          src={destinationLogo}
                          sx={locationAvatar}
                          variant="logo"
                        >
                          {getIcon(
                            destinationCategoryIconPath,
                            destinationIcon,
                            {
                              imageIconProps: {
                                solidColorFilterId:
                                  "directionOverview_avatar_filter",
                              },
                            }
                          ) || destinationIconComp}
                        </Avatar>
                      </Box>
                      <Typography sx={titleText}>
                        {t(destinationName)}
                      </Typography>
                      <CardMedia
                        component="img"
                        sx={venueLogo}
                        src={destinationVenueLogo}
                      />
                    </Box>
                    <Box sx={stepContainer}>
                      <Box sx={stepProgressBarContainer}>
                        <VerticalStepProgressBar
                          steps={steps}
                          currentStep={currentStepNumber}
                          sx={stepProgressBar}
                          handleClickStep={goToStepByIndex}
                          enableAnimateScroll={true}
                          originStepIcon={originMarkerIcon}
                          originStepImage={
                            originVenueLogo && (
                              <ListItemIcon sx={listItemIcon}>
                                <CardMedia
                                  component="img"
                                  image={originVenueLogo}
                                  sx={logoImage}
                                />
                              </ListItemIcon>
                            )
                          }
                          destinationStepIcon={destinationStepIcon}
                          destinationStepImage={
                            destinationVenueLogo && (
                              <ListItemIcon sx={listItemIcon}>
                                <CardMedia
                                  component="img"
                                  image={destinationVenueLogo}
                                  sx={logoImage}
                                />
                              </ListItemIcon>
                            )
                          }
                        />
                      </Box>
                    </Box>
                    <Box sx={actionContainer}>
                      <Button
                        className={clsx("prevButton", isRtl ? "is-rtl" : "")}
                        variant="outlined"
                        sx={playerButton}
                        startIcon={prevIcon}
                        onClick={goToPrevStep}
                        disabled={!hasPrevStep}
                      >
                        {t("Previous")}
                      </Button>
                      {isPlaying ? (
                        <Button
                          variant="outlined"
                          sx={playerButton}
                          startIcon={<PauseIcon />}
                          onClick={pauseAutoPlay}
                          disabled={!isPlaying}
                        >
                          {t("Stop")}
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          sx={playerButton}
                          startIcon={
                            hasNextStep ? (
                              <PlayArrowIcon />
                            ) : (
                              <RefreshArrowIcon />
                            )
                          }
                          onClick={
                            hasNextStep ? resumeAutoPlay : restartAutoPlay
                          }
                        >
                          {hasNextStep ? t("Play") : t("Play Again")}
                        </Button>
                      )}
                      <Button
                        className={clsx("highlight", isRtl ? "is-rtl" : "")}
                        variant="contained"
                        sx={playerButton}
                        endIcon={nextIcon}
                        onClick={goToNextStep}
                        disabled={!hasNextStep}
                      >
                        {t("Next")}
                      </Button>
                    </Box>
                  </CardContent>
                </Box>
              )}
              {isPlaying && (
                <CoolDownProgress
                  sx={cooldownProgress}
                  duration={stepAutoplayConfig.delay}
                  repeat={true}
                  reverse={true}
                />
              )}
            </Box>
          </MotionDiv>
        </PageCard>
      }
    >
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelectMenu}>
          <OrdinalSelector
            levels={currentLevel}
            selectedOrdinal={currentOrdinal}
          />
        </Box>
      )}
      <VerticalDirectionImageDialog
        imageSrc={verticalDirectionImageUrl}
        description={currentStep?.description}
        originLevelName={currentOriginStepLevelName}
        destinationLevelName={currentDestStepLevelName}
        open={showVerticalStepDialog}
      />
    </Layout>
  )
}
